import { isEmpty } from 'lodash';
import { ReactNode, useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import { CanUsesInitialBudgets } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import { GlMasksResponse } from 'src/services/useGetGlMasks';
import { GLCodePeriodBudgetResponse } from 'src/services/useGlCodePeriodBudgetApi';
import { generateRegexPattern } from 'src/utils/strings';
export interface CheckbookLineItemError {
  glCode: string[];
  glCodeFormat: ReactNode[];
  description: string[];
  errorMessages: ReactNode[];
}

export interface CheckbookLineItemWarning {
  generic: string[];
  glCode: string[];
  description: string[];
  budgetAmount: string[];
  isFixed: string[];
  isActive: string[];
  initialBudget: string[];
  warningMessages: string[];
}

export interface CheckbookLineItemInfo {
  glCode: string[];
}

const defaultViewBudgetErrors: CheckbookLineItemError = {
  glCode: [],
  glCodeFormat: [],
  description: [],
  errorMessages: [],
};

const defaultViewBudgetWarnings: CheckbookLineItemWarning = {
  generic: [],
  glCode: [],
  description: [],
  initialBudget: [],
  budgetAmount: [],
  isFixed: [],
  isActive: [],
  warningMessages: [],
};

export const defaultViewBudgetInfos: CheckbookLineItemInfo = {
  glCode: [],
};

const useCheckbookPeriodErrors = (
  items: GLCodePeriodBudgetResponse[],
  originalItems: GLCodePeriodBudgetResponse[],
  allItems: GLCodePeriodBudgetResponse[],
  isEditMode: boolean,
  regexPatternBE: string[],
  glFormatsResponse: GlMasksResponse
) => {
  const usesBudgets = usePermission({ permissions: CanUsesInitialBudgets, companyAttribute: true });

  const errors = useMemo(() => {
    const newlyAddedItems = [...items].filter((item) => item.isNewGlCode);

    return items.map((item) => {
      let error: CheckbookLineItemError = { ...defaultViewBudgetErrors };
      const duplicateGLCodePeriod = [...allItems, ...newlyAddedItems].some(
        (glCode) =>
          item.code &&
          glCode.id !== item.id &&
          glCode.code.toLowerCase().trim() === item.code.toLowerCase().trim()
      );

      if (isEditMode) {
        if (
          item?.isNewGlCode &&
          glFormatsResponse?.glMasks?.length &&
          glFormatsResponse?.setGlMask
        ) {
          const regex = generateRegexPattern(regexPatternBE, item?.code.toUpperCase());
          if (!regex) {
            const acceptableFormats = regexPatternBE?.map((pattern: string, index: number) => (
              <li key={index} className="whitespace-pre-wrap mb-1 list-item list-disc ml-4">
                {pattern}
              </li>
            ));

            const html = (
              <div>
                Invalid GL Code format. Allowed format(s):
                <br />
                <ul>{acceptableFormats}</ul>
              </div>
            );

            error.glCodeFormat = [html];
            error.errorMessages = [...error.errorMessages, html];
          }
        }
        if (item?.isNewGlCode && duplicateGLCodePeriod) {
          error.glCode = ['GL Code must not be a duplicate'];
          error.errorMessages = [...error.errorMessages, 'GL Code must not be a duplicate'];
        }
        if (isEmpty(item.code)) {
          error.glCode = ['GL Code must contain a value'];
          error.errorMessages = [...error.errorMessages, 'GL Code must contain a value'];
        }
        if (item.code.length > 765) {
          error.glCode = ['GL Code is more than 765 characters'];
          error.errorMessages = [...error.errorMessages, 'GL Code is more than 765 characters'];
        }
        if (
          item?.isNewGlCode &&
          glFormatsResponse.glMasks.length === 0 &&
          glFormatsResponse?.setGlMask
        ) {
          error.glCode = ['No format specified. Contact corporate administrator to add format(s).'];
          error.errorMessages = [
            ...error.errorMessages,
            'No format specified. Contact corporate administrator to add format(s).',
          ];
        }
        if (isEmpty(item.description)) {
          error.description = ['Description must contain a value'];
          error.errorMessages = [...error.errorMessages, 'Description must contain a value'];
        } else if (item.description?.length > 4000) {
          error.description = ['Description is more than 4000 characters'];
          error.errorMessages = [
            ...error.errorMessages,
            'Description is more than 4000 characters',
          ];
        }
      }

      return error;
    }, []);
  }, [
    allItems,
    glFormatsResponse?.glMasks,
    glFormatsResponse?.setGlMask,
    isEditMode,
    items,
    regexPatternBE,
  ]);

  const warnings = useMemo(
    () =>
      items.map((item) => {
        let warning: CheckbookLineItemWarning = { ...defaultViewBudgetWarnings };
        const originalGLCodePeriod = originalItems.find((glCode) => glCode.rowId === item.rowId);

        if (isEditMode) {
          if (!item.isNewGlCode) {
            if (item.description !== originalGLCodePeriod?.description) {
              warning.description = ['Description has unsaved changes'];
              warning.warningMessages = [
                ...warning.warningMessages,
                'Description has unsaved changes',
              ];
            }
            if (
              usesBudgets &&
              (originalGLCodePeriod?.initialBudget === null
                ? item.initialBudget !== null && (item.initialBudget as any) !== ''
                : Number(item.initialBudget) !== Number(originalGLCodePeriod?.initialBudget))
            ) {
              warning.initialBudget = ['Budget Amount has unsaved changes'];
              warning.warningMessages = [
                ...warning.warningMessages,
                'Budget Amount has unsaved changes',
              ];
            }
            if (Number(item.budgetAmount) !== Number(originalGLCodePeriod?.budgetAmount)) {
              warning.budgetAmount = ['Forecast Amount has unsaved changes'];
              warning.warningMessages = [
                ...warning.warningMessages,
                'Forecast Amount has unsaved changes',
              ];
            }
            if (item.isFixed !== originalGLCodePeriod?.isFixed) {
              warning.isFixed = ['Fixed Code has unsaved changes'];
              warning.warningMessages = [
                ...warning.warningMessages,
                'Fixed Code has unsaved changes',
              ];
            }
            if (item.isActive !== originalGLCodePeriod?.isActive) {
              warning.isActive = ['Active Code has unsaved changes'];
              warning.warningMessages = [
                ...warning.warningMessages,
                'Active Code has unsaved changes',
              ];
            }
          } else {
            warning.generic = ['Newly added GL code'];
            warning.warningMessages = [...warning.warningMessages, 'Newly added GL code'];
            warning.description = ['Description has unsaved changes'];
            warning.warningMessages = [
              ...warning.warningMessages,
              'Description has unsaved changes',
            ];
            usesBudgets
              ? (warning.initialBudget = ['Budget Amount has unsaved changes'])
              : (warning.initialBudget = ['']);
            usesBudgets
              ? (warning.warningMessages = [
                  ...warning.warningMessages,
                  'Budget Amount has unsaved changes',
                ])
              : (warning.initialBudget = ['']);
            warning.budgetAmount = ['Forecast Amount has unsaved changes'];
            warning.warningMessages = [
              ...warning.warningMessages,
              'Forecast Amount has unsaved changes',
            ];
            warning.isFixed = ['Fixed Code has unsaved changes'];
            warning.warningMessages = [
              ...warning.warningMessages,
              'Fixed Code has unsaved changes',
            ];
            warning.isActive = ['Active Code has unsaved changes'];
            warning.warningMessages = [
              ...warning.warningMessages,
              'Active Code has unsaved changes',
            ];
          }
        }

        return warning;
      }),
    [isEditMode, items, originalItems, usesBudgets]
  );

  const infos = useMemo(
    () =>
      items.map((item) => {
        let messages: CheckbookLineItemInfo = { ...defaultViewBudgetInfos };
        return messages;
      }),
    [items]
  );

  const periodErrorList: string[] | ReactNode[] = errors.map((item) => item.errorMessages).flat();
  const periodWarningList: string[] = warnings.map((item) => item.warningMessages).flat();
  const periodInformationList: string[] = infos.map((item) => item.glCode).flat();

  const hasUnsavedPeriodChanges = useMemo(
    () => periodWarningList.some((item) => item.toLowerCase().includes('unsaved')),
    [periodWarningList]
  );

  const removeDuplicates = (errorList: any[]) => {
    const seen = new Set();
    const removedDuplicates = errorList.filter((item) => {
      const itemString =
        typeof item === 'object' ? ReactDOMServer.renderToStaticMarkup(item) : JSON.stringify(item);
      if (seen.has(itemString)) {
        return false;
      } else {
        seen.add(itemString);
        return true;
      }
    });
    return removedDuplicates;
  };

  const uniquePeriodErrorList = removeDuplicates(periodErrorList);
  const hasPeriodErrors = useMemo(() => !!periodErrorList?.length, [periodErrorList?.length]);
  const hasPeriodWarnings = useMemo(() => !!periodWarningList?.length, [periodWarningList?.length]);
  return {
    errors,
    warnings,
    infos,
    periodErrorList,
    uniquePeriodErrorList,
    periodInformationList,
    periodWarningList,
    hasPeriodErrors,
    hasPeriodWarnings,
    hasUnsavedPeriodChanges,
  };
};

export default useCheckbookPeriodErrors;
